import { FGStorefrontGraphQL } from "@fgtheme/graphql";

/** @type {*} */
const Swiper = window.Swiper;

/**
 * @typedef ProductVariantGraphQL
 * @type {{ 
 *  data: { 
 *    nodes: {
 *      id: string, 
 *      title: string,
 *      price: {
 *        amount: string
 *      },
 *      compareAtPrice: {
 *        amount: string
 *      }
 *      product: {
 *        id: string,
 *        title: string,
 *        featuredImage: {
 *          altText: string | null,
 *          url: string
 *        }
 *        tags: string[],
 *        handle: string
 *      }
 *    }[] 
 *  } 
 * }}
 */

/**
 * @typedef FGRecoommendationsProductConfig
 * @type {{ 
 *  mainProduct: { 
 *    title: string,
 *    productId: string,
 *    variantId: string,
 *  },
 *  recommendationProducts: {
 *    title: string,
 *    productId: string
 *  }[]
 * }}
 */

/**
 * @typedef FGRecommendationsComponentConfig
 * @type {{ 
 *  numberOfRecs: number
 * }}
 */

/**
 * 
 * @param {any} Base 
 * @returns 
 */
class FGRecommendationFetcher extends HTMLElement {
  abortController = new AbortController();

  constructor() {
    super();
  }

  /**
   * 
   * @param {string} productId - product ID without the shopify gid://
   */
  async getShopifyProductRecommendations(productId) {
    const response = await fetch(`${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}`, {
      signal: this.abortController.signal
    });
    /**
     * @type {{products: any[], intent: string}}
     */
    const result = await response.json();
    const filtered = this.filterShopifyProductRecommendations(result.products);
    return filtered;
  }

  /**
   * 
   * @param {any[]} shopifyRecommendations 
   */
  filterShopifyProductRecommendations(shopifyRecommendations) {
    return shopifyRecommendations.filter(item => {
      // No unavailable items
      if (!item.available) {
        return false;
      }
      // No items with a selling plan
      if (item.selling_plan_groups.length > 0) {
        return false;
      }
      // No items with multiple variants
      if (item.variants.length > 1) {
        return false;
      }
      return true;
    });
  }

  /**
   * Calls the shopify product recommendation api for each product id provided.
   * 
   * @param {string[]} productIds - product ID without the shopify gid://
   */
  async getProductRecommendationVariantIds(productIds) {
    const allVariantIds = new Set();

    for (let ii = 0; ii < productIds.length; ii++) {
      const productId = productIds[ii];
      if (!productId) {
        continue;
      }
      const recommendations = await this.getShopifyProductRecommendations(productId);
      const variantIds = recommendations.map(item => item.variants[0].id);
      variantIds.forEach(id => allVariantIds.add(id.toString()));
    }

    return allVariantIds;
  }

  /**
   * 
   * @param {string[]} variantIds - array of product variant ID without the shopify gid://
   * @returns {Promise<ProductVariantGraphQL["data"]["nodes"]>}
   */
  async getProductVariantData(variantIds) {
    const formattedIds = variantIds.map(id => `gid://shopify/ProductVariant/${id}`);

    const payload = {
      query: this.getNodesQuery(),
      variables: { ids: formattedIds }
    };
    const response = await FGStorefrontGraphQL(payload, this.abortController);
    const result = await response?.json();
    if (result?.data?.nodes) {
      return result.data.nodes;
    }
    return [];
  }

  getNodesQuery() {
    return `
      query ProductVariantNodes($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            title
            price {
              amount
            }
            compareAtPrice {
              amount
            }
            product {
              id
              title
              featuredImage {
                altText
                url
              }
              tags
              handle
            }
          }
        }
      }
    `;
  }
}

class FGRecommendationComponent extends FGRecommendationFetcher {
  /**
   * @type {FGRecommendationsComponentConfig}
   */
  componentConfig = {
    numberOfRecs: 2
  };

  /**
   * @type {ProductVariantGraphQL["data"]["nodes"]}
   */
  recommendations = [];

  constructor() {
    super();
  }

  async connectedCallback() {
    const productConfigEl = this.querySelector('[data-config="products"]');
    if (!productConfigEl) {
      console.error('No product configuration found');
      return;
    }

    /**
     * @type {FGRecoommendationsProductConfig | null}
     */
    let productConfig = null;
    try {
      productConfig = JSON.parse(productConfigEl.textContent);
      if (!productConfig) throw new Error("Product Config is null");
    } catch (e) {
      console.error('JSON parse fail for product config', e);
      return;
    }

    const componentConfigEl = this.querySelector('[data-config="configuration"]');
    if (!componentConfigEl) {
      console.error('No component config found');
      return;
    }

    let componentConfig = null;
    try {
      componentConfig = JSON.parse(componentConfigEl.textContent);
    } catch (e) {
      console.error('JSON parse fail for component config', e);
      return;
    }
    if (!componentConfig) {
      console.error('Component config is null');
      return;
    }
    this.componentConfig = { ...this.componentConfig, ...componentConfig };

    const productIds = productConfig.recommendationProducts.map(item => item.productId);

    const recommendedVariantIds = await this.getProductRecommendationVariantIds(productIds);

    const variantIds = this.prepareVariantDataIds(productConfig, recommendedVariantIds);
    if (!variantIds || variantIds.length === 0) {
      console.warn('No recommendations found.');
      return;
    }

    this.recommendations = await this.getProductVariantData(variantIds);
    this.modifyRecommendations();
    if (!this.recommendations || this.recommendations.length === 0) {
      console.warn('No variant data retrieved.');
      return;
    }
  }

  /**
   * 
   * @param {FGRecoommendationsProductConfig} productConfig 
   * @param {Set<string>} recommendedVariantIds 
   * @returns {string[]}
   */
  prepareVariantDataIds(productConfig, recommendedVariantIds) {
    // Remove the main product variant id if it was included in recommened products.
    return [...recommendedVariantIds].filter(id => id !== productConfig.mainProduct.variantId);
  }

  /**
   * Use this in a component that inherits from this one in order to modify
   * the product data before rendering
   */
  modifyRecommendations() {
    // noop
  }
}

class FGAlsoLike extends FGRecommendationComponent {
  /**
   * @type {any | null}
   */
  containerTplEl = null;

  /**
   * @type {any | null}
   */
  cardTplEl = null;

  constructor() {
    super();
  }

  async connectedCallback() {
    await super.connectedCallback();

    if (!this.recommendations || this.recommendations.length === 0) {
      return;
    }

    this.containerTplEl = this.querySelector('template[data-tpl="container"]');
    if (!this.containerTplEl) {
      console.error('No container template found.');
      return;
    }

    this.cardTplEl = this.querySelector('template[data-tpl="card"]');
    if (!this.cardTplEl) {
      console.error('No product card template found');
      return;
    }

    const containerTpl = this.containerTplEl.content.cloneNode(true);

    containerTpl.querySelector('[data-cards]').append(...this.renderCards());
    this.appendChild(containerTpl);
  }

  renderCards() {
    return this.recommendations.map((item, index) => {
      const cardTpl = this.cardTplEl.content.cloneNode(true);
      this.renderCard(item, index, cardTpl);

      return cardTpl;
    });
  }

  /**
   * 
   * @param {ProductVariantGraphQL["data"]["nodes"][0]} item 
   * @param {number} index 
   * @param {any} cardTpl 
   */
  renderCard(item, index, cardTpl) {
    const cardContainer = cardTpl.querySelector('[data-card-container');
    cardContainer.setAttribute('data-variant-id', item.id);

    const imageEl = cardTpl.querySelector('[data-image]');
    imageEl.setAttribute('src', item.product.featuredImage.url);
    imageEl.setAttribute('alt', item.product.featuredImage.altText);

    cardTpl.querySelector('[data-title]').textContent = item.product.title;

    const prodId = item.product.id.substring(item.product.id.lastIndexOf('/') + 1);
    const priceEl = cardTpl.querySelector('[data-price]');
    priceEl.textContent = `$${parseFloat(item.price.amount).toFixed(2)}`;
    priceEl.dataset.slAttributeP = `${prodId}`;
    if (item.compareAtPrice && item.compareAtPrice.amount) {
      const capEl = cardTpl.querySelector('[data-compare-at-price]');
      capEl.textContent = `$${parseFloat(item.compareAtPrice.amount).toFixed(2)}`;
      capEl.dataset.slAttributeCap = `${prodId}`;
    }

    cardTpl.querySelector('[data-atc-button]').addEventListener('click', (/** @type {any} */ event) => {
      this.handleAddToCart(event, item);
    });
    this.customRenderCard(item, index, cardTpl);
  }

  /**
   * Override to customize card per component
   * @param {ProductVariantGraphQL["data"]["nodes"][0]} item 
   * @param {number} index 
   * @param {any} cardTpl 
   */
  customRenderCard(item, index, cardTpl) {
    // noop
  }

  /**
   * @param {any} event
   * @param {ProductVariantGraphQL["data"]["nodes"][0]} item 
   */
  async handleAddToCart(event, item) {
    event.preventDefault();
    event.stopPropagation();
    const atcButton = event.currentTarget;

    const itemToAdd = {
      id: item.id.replace('gid://shopify/ProductVariant/', ''),
      quantity: 1,
      properties: this.getLineItemProperties()
    }

    const requestBody = {
      items: [itemToAdd],
      sections: '',
    };
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    requestBody.sections = cartItemComponentsSectionIds.join(',');
    atcButton.setAttribute('data-added', true);
    this.disableAddToCartButtons();
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    const result = await response.json();
    const newEvent = new CustomEvent('cart:update', { detail: { data: { sections: result.sections } } });
    setTimeout(() => {
      atcButton.setAttribute('data-added', false);
      this.enableAddToCartButtons();
      document.dispatchEvent(newEvent);
    }, 500);
  }

  disableAddToCartButtons() {
    const atcButtons = this.querySelectorAll('[data-atc-button]');
    atcButtons.forEach(( /** @type {any} */ button) => {
      button.disabled = true;
    });
  }

  enableAddToCartButtons() {
    const atcButtons = this.querySelectorAll('[data-atc-button]');
    atcButtons.forEach(( /** @type {any} */button) => {
      button.disabled = false;
    });
  }

  getLineItemProperties() {
    return {
      '_fg_widget_id': 'fg-also-like'
    }
  }
}

class FGBoughtTogether extends FGRecommendationComponent {
  /**
   * @type {any | null}
   */
  containerTplEl = null;

  /**
   * @type {any | null}
   */
  cardTplEl = null;

  /**
   * @type {any | null}
   */
  addToCartButton = null;

  /**
   * @type {Set<ProductVariantGraphQL["data"]["nodes"][0]>}
   */
  selectedVariants = new Set();

  constructor() {
    super();
  }

  async connectedCallback() {
    await super.connectedCallback();

    if (!this.recommendations || this.recommendations.length === 0) {
      return;
    }

    this.containerTplEl = this.querySelector('template[data-tpl="container"]');
    if (!this.containerTplEl) {
      console.error('No container template found.');
      return;
    }

    this.cardTplEl = this.querySelector('template[data-tpl="card"]');
    if (!this.cardTplEl) {
      console.error('No product card template found');
      return;
    }

    const containerTpl = this.containerTplEl.content.cloneNode(true);

    this.addToCartButton = containerTpl.querySelector('[data-atc-button]');
    this.addToCartButton.addEventListener('click', () => {
      this.handleAddToCart();
    });

    containerTpl.querySelector('[data-cards]').append(...this.renderCards());
    this.appendChild(containerTpl);
    this.updatePriceTotals();
  }

  renderCards() {
    return this.recommendations.map((item, index) => {
      this.addSelectedVariant(item);

      const cardTpl = this.cardTplEl.content.cloneNode(true);
      this.renderCard(item, index, cardTpl);

      return cardTpl;
    });
  }

  /**
   * 
   * @param {ProductVariantGraphQL["data"]["nodes"][0]} item 
   * @param {number} index 
   * @param {any} cardTpl 
   */
  renderCard(item, index, cardTpl) {
    const cardContainer = cardTpl.querySelector('[data-card-container');
    cardContainer.setAttribute('data-variant-id', item.id);
    if (index === 0) {
      cardContainer.classList.add('fg-bt-primary-product');
    }
    const checkboxEl = cardTpl.querySelector('[data-selected-checkbox]');
    checkboxEl.addEventListener('change', (/** @type {*} */ event) => {
      this.handleCheckboxChange(event, item);
    })
    cardContainer.addEventListener('click', (/** @type {*} */ event) => {
      // Let clicks on the anchor and checkbox through 
      const tagName = event.target.tagName;
      if (tagName === 'A' || tagName === "INPUT") {
        return;
      }
      // Toggle selected state via card click
      const checkboxEl = cardContainer.querySelector('input[type="checkbox"]');
      if (checkboxEl.checked) {
        checkboxEl.checked = false;
        this.removeSelectedVariant(item);
      } else {
        checkboxEl.checked = true;
        this.addSelectedVariant(item);
      }
      this.updatePriceTotals();
    });
    const imageEl = cardTpl.querySelector('[data-image]');
    imageEl.setAttribute('src', item.product.featuredImage.url);
    imageEl.setAttribute('alt', item.product.featuredImage.altText);

    if (index > 0) {
      const titleAnchor = document.createElement('a');
      titleAnchor.setAttribute('href', `/products/${item.product.handle}`);
      titleAnchor.textContent = item.product.title;
      cardTpl.querySelector('[data-title]').appendChild(titleAnchor);
    } else {
      cardTpl.querySelector('[data-title]').textContent = item.product.title;
    }

    const prodId = item.product.id.substring(item.product.id.lastIndexOf('/') + 1);
    const priceEl = cardTpl.querySelector('[data-price]');
    priceEl.textContent = `$${parseFloat(item.price.amount).toFixed(2)}`;
    priceEl.dataset.slAttributeP = `${prodId}`;
    if (item.compareAtPrice && item.compareAtPrice.amount) {
      const capEl = cardTpl.querySelector('[data-compare-at-price]');
      capEl.textContent = `$${parseFloat(item.compareAtPrice.amount).toFixed(2)}`;
      capEl.dataset.slAttributeCap = `${prodId}`;
    }
  }

  /**
   * 
   * @param {*} event 
   * @param {*} item 
   */
  handleCheckboxChange(event, item) {
    const checked = event.target.checked;
    if (!checked) {
      this.removeSelectedVariant(item);
    } else {
      this.addSelectedVariant(item);
    }
    this.updatePriceTotals();
  }

  getLineItemProperties() {
    return {
      '_fg_widget_id': 'fg-bought-together'
    }
  }

  async handleAddToCart() {
    const atcButton = this.querySelector('[data-atc-button]');
    if (!atcButton) {
      console.error('No ATC Button found');
      return;
    }
    const itemsToAdd = [...this.selectedVariants].map(variant => {
      return {
        id: variant.id.replace('gid://shopify/ProductVariant/', ''),
        quantity: 1,
        properties: this.getLineItemProperties()
      };
    })
    const requestBody = {
      items: itemsToAdd,
      sections: '',
    };
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    requestBody.sections = cartItemComponentsSectionIds.join(',');
    this.addToCartButton.setAttribute('data-added', true);
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    const result = await response.json();
    const newEvent = new CustomEvent('cart:update', { detail: { data: { sections: result.sections } } });
    setTimeout(() => {
      this.addToCartButton.setAttribute('data-added', false);
      document.dispatchEvent(newEvent);
    }, 500);
  }

  /**
   * 
   * @param {ProductVariantGraphQL["data"]["nodes"][0]} variant 
   */
  addSelectedVariant(variant) {
    this.selectedVariants.add(variant);
  }

  /**
   * 
   * @param {ProductVariantGraphQL["data"]["nodes"][0]} variant 
   */
  removeSelectedVariant(variant) {
    this.selectedVariants.delete(variant);
  }

  updatePriceTotals() {
    const totalPrice = this.getTotalPrice();
    const totalCompareAtPrice = this.getTotalCompareAtPrice();
    const totalPriceEl = this.querySelector('[data-total-price]');
    const totalCompareAtPriceEl = this.querySelector('[data-total-compare-at-price]');
    if (!totalPriceEl || !totalCompareAtPriceEl) {
      console.error('Price total elements not found');
      return;
    }
    totalPriceEl.textContent = `$${totalPrice.toFixed(2)}`;
    totalCompareAtPriceEl.textContent = `$${totalCompareAtPrice.toFixed(2)}`;
    if (totalPrice === 0) {
      this.addToCartButton.disabled = true;
    } else {
      this.addToCartButton.disabled = false;
    }
  }

  getTotalPrice() {
    return [...this.selectedVariants].reduce((acc, cur) => {
      return acc + parseFloat(cur.price.amount);
    }, 0);
  }

  getTotalCompareAtPrice() {
    return [...this.selectedVariants].reduce((acc, cur) => {
      if (!cur.compareAtPrice || !cur.compareAtPrice.amount) {
        return acc + parseFloat(cur.price.amount);
      }
      return acc + parseFloat(cur.compareAtPrice.amount);
    }, 0);
  }

  /**
   * 
   * @param {FGRecoommendationsProductConfig} productConfig 
   * @param {Set<string>} recommendedVariantIds 
   * @returns {string[]}
   */
  prepareVariantDataIds(productConfig, recommendedVariantIds) {
    // Remove the main product variant id if it was included in recommened products.
    const variantIds = [...recommendedVariantIds].filter(id => id !== productConfig.mainProduct.variantId);
    if (!variantIds || variantIds.length === 0) {
      return [];
    }

    // Put the main product variant back at the start of the array so it's the first item to render in the list
    variantIds.unshift(productConfig.mainProduct.variantId);
    variantIds.splice(1 + this.componentConfig.numberOfRecs);
    return variantIds;
  }
}

if (!customElements.get('fg-rec-component')) {
  customElements.define('fg-rec-component', FGRecommendationComponent);
}

if (!customElements.get('fg-also-like')) {
  customElements.define('fg-also-like', FGAlsoLike);
}

if (!customElements.get('fg-bought-together')) {
  customElements.define('fg-bought-together', FGBoughtTogether);
}

export { FGAlsoLike, FGBoughtTogether, FGRecommendationFetcher }